import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAdminResumoIdentificadoresQuery, type IdentificadorLoja, type TipoIdentificador } from '../../api';
import { montarUrlIdentificador } from '../../lib/url-identificador';
import { DialogConfigurarTablet } from './dialog-configurar-tablet';
import { DialogPontosAcesso } from './dialog-pontos-acesso';
import { DialogQrCode } from './dialog-qrcode';
import { DialogParearDispositivo } from './dialog-parear-dispositivo';
import { ehTipoPareavel } from '../../lib/tipos-atendimento';
import { IconeTipoIdentificador, ROTULO_TIPO_IDENTIFICADOR } from './icone-modo';

/** Acima disso a UI mostra os primeiros + "Ver todos (N)" em vez de listar tudo. */
const LIMITE_UI = 6;

interface PontosDeAcessoCardProps {
  idConfig: number;
  /** Tipos relevantes a esta aba (ex.: ['DELIVERY','VITRINE']). */
  tipos: TipoIdentificador[];
  /**
   * Máximo de itens exibidos na amostra (default 6). Tipos com muitos pontos
   * (mesa/comanda) passam um limite menor; acima dele a UI avisa que a lista
   * está limitada e leva ao "Gerenciar pontos de acesso".
   */
  limite?: number;
  /**
   * Troca o botão "QR code" pelo "Configurar tablet" — o passo a passo de kiosk,
   * que já mostra o mesmo QR no primeiro passo. Só a aba do tablet liga isso: o
   * roteiro é do aparelho na mesa, não serve para delivery/vitrine/mesa.
   */
  guiaTablet?: boolean;
}

/**
 * Card de "Pontos de acesso" exibido no topo das abas de configuração. Padrão
 * único para todos os tipos: lista uma amostra dos identificadores da loja
 * (abrir/copiar o link) e um botão "Gerenciar pontos de acesso" (sempre visível)
 * que abre a tela de acessos em um modal, recortada aos tipos desta aba.
 */
export function PontosDeAcessoCard({
  idConfig,
  tipos,
  limite = LIMITE_UI,
  guiaTablet = false,
}: PontosDeAcessoCardProps): JSX.Element {
  const { data, isLoading } = useAdminResumoIdentificadoresQuery(idConfig);
  const [copiado, setCopiado] = useState(false);
  /** Ponto cujo QR está aberto — `null` = fechado. */
  const [verQrDe, setVerQrDe] = useState<IdentificadorLoja | null>(null);
  /** Ponto cujo passo a passo de tablet está aberto — `null` = fechado. */
  const [configurarDe, setConfigurarDe] = useState<IdentificadorLoja | null>(null);
  /** Modal de pontos de acesso aberto direto no formulário de criação. */
  const [criandoPonto, setCriandoPonto] = useState(false);
  /** Ponto cujo código de pareamento está aberto — `null` = fechado. */
  const [parearDe, setParearDe] = useState<IdentificadorLoja | null>(null);

  async function copiar(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
    } catch {
      /* clipboard indisponível — ignora silenciosamente */
    }
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  const resumo = data.resumo;
  const totalAtivos = tipos.reduce((soma, t) => soma + (resumo[t]?.ativos ?? 0), 0);

  const itens = tipos.flatMap((t) => resumo[t]?.itens ?? []);
  const mostrados = itens.slice(0, limite);
  const temMais = totalAtivos > mostrados.length;

  return (
    <>
      {totalAtivos === 0 ? (
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            Nenhum ponto de acesso configurado ainda.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCriandoPonto(true)}>
            Criar ponto de acesso
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {mostrados.map((item) => {
            const url = montarUrlIdentificador(item);
            return (
              <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        flexShrink: 0,
                        color: 'primary.main',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(241,11,92,0.15)' : 'rgba(241,11,92,0.08)',
                      })}
                    >
                      <IconeTipoIdentificador tipo={item.tipo} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                        {item.numero?.trim() || ROTULO_TIPO_IDENTIFICADOR[item.tipo]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {url}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ flexShrink: 0 }}
                    justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}
                  >
                    {/* Copiar e QR são ícone puro: repetidos em cada linha da
                        lista, com rótulo viravam parede de texto e roubavam a
                        vez de "Configurar totem"/"Abrir", que são as decisões
                        da tela. O tooltip (e o aria-label) mantêm o nome. */}
                    <Tooltip title="Copiar link">
                      <IconButton size="small" aria-label="Copiar link" onClick={() => copiar(url)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* No tablet o QR não é para imprimir: é para a câmera do
                        aparelho ler UMA vez, no passo 1 do roteiro. Por isso ele
                        mora lá dentro, e não em um botão à parte. */}
                    {!guiaTablet && (
                      <Tooltip title="Ver QR code">
                        <IconButton size="small" aria-label="Ver QR code" onClick={() => setVerQrDe(item)}>
                          <QrCode2Icon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {ehTipoPareavel(item.tipo) && (
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setParearDe(item)}
                      >
                        Configurar totem
                      </Button>
                    )}
                    <Button
                      size="small"
                      color={guiaTablet ? 'inherit' : 'primary'}
                      startIcon={<OpenInNewIcon />}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Abrir
                    </Button>
                    {guiaTablet && (
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setConfigurarDe(item)}
                      >
                        Configurar tablet
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
          {temMais && (
            <Typography variant="caption" color="text.secondary">
              Mostrando {mostrados.length} de {totalAtivos}. Veja e gerencie todos em "Gerenciar pontos de acesso".
            </Typography>
          )}
        </Stack>
      )}

      <Snackbar open={copiado} autoHideDuration={2000} onClose={() => setCopiado(false)} message="Link copiado" />

      <DialogQrCode identificador={verQrDe} aoFechar={() => setVerQrDe(null)} />

      <DialogParearDispositivo identificador={parearDe} aoFechar={() => setParearDe(null)} />

      <DialogConfigurarTablet identificador={configurarDe} aoFechar={() => setConfigurarDe(null)} />

      <DialogPontosAcesso
        aberto={criandoPonto}
        aoFechar={() => setCriandoPonto(false)}
        idConfig={idConfig}
        tipos={tipos}
        criarAoAbrir
      />
    </>
  );
}

/**
 * Abre a gestão dos pontos de acesso DESTA aba. Pensado para o CANTO DIREITO do
 * cabeçalho da seção (prop `acoes` da `Secao`), e não no rodapé do card: ali ele
 * empurrava a lista para baixo e disputava atenção com o "Copiar link"/"Abrir"
 * de cada ponto, que são as ações do dia a dia.
 *
 * Abre em MODAL, e não navegando para a tela de acessos: quem está configurando
 * um modo veria lá a loja inteira para mexer em um tipo só, e ainda perderia as
 * alterações não salvas da configuração no caminho.
 *
 * Sem `variant` de propósito — botão de texto, para não competir com o "Salvar
 * alterações" da barra fixa, que é a ação primária da tela.
 *
 * Some quando a loja não tem nenhum ponto: nesse caso o próprio card já oferece
 * "Criar ponto de acesso", e dois botões diriam a mesma coisa. Com o modal
 * aberto ele fica: inativar o último ponto lá dentro zera a contagem, e sumir
 * nessa hora arrancaria o modal da tela no meio do uso.
 *
 * A consulta é a mesma do card — o RTK Query deduplica, então não há
 * requisição extra por ter dois componentes lendo o mesmo resumo.
 */
export function BotaoGerenciarPontosDeAcesso({
  idConfig,
  tipos,
}: {
  idConfig: number;
  tipos: TipoIdentificador[];
}): JSX.Element | null {
  const { data } = useAdminResumoIdentificadoresQuery(idConfig);
  const [aberto, setAberto] = useState(false);

  const totalAtivos = tipos.reduce((soma, t) => soma + (data?.resumo[t]?.ativos ?? 0), 0);
  if (totalAtivos === 0 && !aberto) return null;

  return (
    <>
      <Button
        size="small"
        startIcon={<TuneIcon />}
        onClick={() => setAberto(true)}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        Gerenciar pontos de acesso
      </Button>
      <DialogPontosAcesso aberto={aberto} aoFechar={() => setAberto(false)} idConfig={idConfig} tipos={tipos} />
    </>
  );
}
